import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Minus, Plus, Search, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import { useCart } from "@/context/CartContext";
import { useSchedule } from "@/context/ScheduleContext";
import { Input } from "@/components/ui/input";
import ProtectedImage from "@/components/ProtectedImage";
import RangeCalendar from "@/components/RangeCalendar";
import ScheduleStrip from "@/components/ScheduleStrip";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "-");

function stockText(item, sched) {
  if (item.status === "MAINTENANCE")
    return { text: `Tidak tersedia: ${item.condition || "maintenance"}`, tone: "out" };
  if (!sched.ok)
    return { text: "Pilih jadwal untuk cek ketersediaan", tone: "plain" };
  const available = item.available_qty;
  if (available == null) return { text: "Ketersediaan untuk jadwal ini belum tersedia", tone: "plain" };
  if (available <= 0) return { text: "Habis untuk jadwal ini", tone: "out" };
  if (available <= 1) return { text: `Sisa ${available} unit untuk jadwal ini`, tone: "low" };
  return { text: `Sisa ${available} unit untuk jadwal ini`, tone: "ok" };
}

const toneClass = {
  ok: "text-emerald-700 font-normal",
  low: "text-amber-800 font-normal",
  out: "text-rose-700 font-bold",
  plain: "text-slate-500",
};

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [serverCats, setServerCats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("nama-asc");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const { setQty, qtyOf, totalQty, addLines } = useCart();
  const sched = useSchedule();
  const titleRef = useRef(null);

  const rangeParams = sched.ok
    ? { start_time: sched.pickup, end_time: sched.ret }
    : {};

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = { q, ...(sched.ok ? { start_time: sched.pickup, end_time: sched.ret } : {}) };
      const [{ data }, full] = await Promise.all([
        api.get("/items", { params }),
        q ? api.get("/items", { params: rangeParams }) : Promise.resolve(null),
      ]);
      setItems(data);
      if (full) setAllItems(full.data);
      else if (!q) setAllItems(data);
    } catch (e) {
      setLoadError(true);
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, sched.pickup, sched.ret, sched.ok]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/templates").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setServerCats(data.map((c) => c.name ?? c))).catch(() => setServerCats(null));
  }, []);

  // Kembalikan posisi scroll saat kembali dari detail.
  useEffect(() => {
    try {
      const y = Number(sessionStorage.getItem("dexter.catalog.scroll"));
      if (Number.isFinite(y) && y > 0) requestAnimationFrame(() => window.scrollTo(0, y));
    } catch { /* abaikan */ }
  }, []);

  const categories = useMemo(() => {
    if (serverCats) return serverCats;
    return [...new Set(allItems.map((i) => i.category))];
  }, [serverCats, allItems]);

  const counts = useMemo(() => {
    const m = new Map();
    allItems.forEach((i) => m.set(i.category, (m.get(i.category) || 0) + 1));
    return m;
  }, [allItems]);

  const shown = useMemo(() => {
    let rows = category ? items.filter((i) => i.category === category) : [...items];
    const avail = (i) => i.available_qty ?? -1;
    if (sort === "nama-desc") rows.sort((a, b) => b.name.localeCompare(a.name, "id"));
    else if (sort === "sisa-desc" && sched.ok) rows.sort((a, b) => avail(b) - avail(a));
    else rows.sort((a, b) => a.name.localeCompare(b.name, "id"));
    return rows;
  }, [items, category, sort, sched.ok]);

  const pakaiPaket = (t) => {
    if (t.requires_correction || t.lines.some(l => l.is_bookable === false || l.is_archived || l.item_code === "RGN-001" || l.category?.trim().toLowerCase() === "ruangan")) return toast.error("Paket lama berisi lokasi/alat yang tidak bisa dipesan. Ubah paket dan pilih lokasi pada Pilih Jadwal.");
    addLines(t.lines);
    toast.success(`Paket "${t.name}" masuk keranjang`);
  };

  const rememberScroll = () => {
    try { sessionStorage.setItem("dexter.catalog.scroll", String(window.scrollY)); } catch { /* abaikan */ }
  };

  const gateApply = (next) => {
    sched.patch(next);
    toast.success("Jadwal diterapkan. Daftar alat langsung tampil.");
    setTimeout(() => titleRef.current?.focus(), 0);
  };

  // Gate schedule-first: tanpa jadwal aktif, daftar/pencarian/filter/sort
  // tidak dirender — hanya blok pemilih jadwal terpusat.
  if (!sched.ok) {
    return (
      <div className="pb-24">
        <h1 ref={titleRef} tabIndex={-1} className="sr-only">
          Katalog alat
        </h1>
        <section aria-labelledby="gate-judul" className="mx-auto max-w-[560px] rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
          <h2 id="gate-judul" tabIndex={-1} className="font-heading text-[20px] font-semibold text-slate-900">
            Pilih jadwal peminjaman dulu
          </h2>
          <p className="mb-3 mt-1 max-w-[60ch] text-sm text-slate-500">
            Katalog dibuka setelah ada jadwal ambil dan kembali (WIB).
          </p>
          <RangeCalendar initial={sched.draft} submitLabel="Pilih jadwal & lanjut"
            showCommitted={false} months={1} compact onApply={gateApply} onCancel={() => {}} />
        </section>
      </div>
    );
  }

  return (
    <div className="pb-24 max-lg:flex max-lg:flex-col">
      <h1 ref={titleRef} tabIndex={-1} className="sr-only">
        Katalog alat
      </h1>

      <ScheduleStrip />

      {templates.length > 0 && (
        <div className="mt-6 space-y-3 max-lg:order-4 max-lg:mt-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Template Paket</h2>
            <Link to="/paket" data-testid="manage-templates-link" className="text-sm font-medium text-[#004f9e] hover:underline">
              Paket Lainnya
            </Link>
          </div>
          <div data-testid="template-list" className="flex flex-nowrap gap-2 overflow-x-auto pb-1 sm:gap-3 lg:flex-wrap">
            {templates.map((t) => (
              <button key={t.id} data-testid={`use-template-${t.id}`} onClick={() => pakaiPaket(t)}
                className="flex min-h-[44px] items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm text-slate-900 transition-colors duration-200 hover:border-[#0067c8]">
                <Bookmark className="h-4 w-4 text-[#0067c8]" />
                <span className="font-medium">{t.name}</span>
                <span className="font-mono text-xs text-slate-500">{t.lines.reduce((s, l) => s + l.qty, 0)} pcs</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-2 grid grid-cols-1 items-start gap-4 sm:mt-6 lg:grid-cols-[208px_1fr]">
        <nav aria-label="Kategori alat" className="min-w-0 rounded-lg border border-slate-200 bg-white p-2 sm:p-4 lg:sticky lg:top-20">
          <h2 className="mb-2.5 text-sm font-semibold text-slate-900 max-lg:sr-only">Kategori</h2>
          <ul className="grid gap-1 max-lg:flex max-lg:flex-nowrap max-lg:gap-2 max-lg:overflow-x-auto max-lg:pb-1">
            <li className="max-lg:shrink-0">
              <button data-testid="filter-all" onClick={() => setCategory("")}
                aria-pressed={!category}
                className={`flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition-colors duration-200 max-lg:w-auto max-lg:border max-lg:border-slate-300 ${!category ? "border border-[#c4dcf5] bg-[#e7f1fc] font-bold text-slate-900" : "border border-transparent text-slate-700 hover:bg-slate-100"}`}>
                <span>Semua</span>
                <span className="text-[13px] tabular-nums text-slate-500">{allItems.length}</span>
              </button>
            </li>
            {categories.map((c) => (
              <li key={c} className="max-lg:shrink-0">
                <button data-testid={`filter-${slug(c)}`} onClick={() => setCategory(category === c ? "" : c)}
                  aria-pressed={category === c}
                  className={`flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition-colors duration-200 max-lg:w-auto max-lg:border max-lg:border-slate-300 ${category === c ? "border border-[#c4dcf5] bg-[#e7f1fc] font-bold text-slate-900" : "border border-transparent text-slate-700 hover:bg-slate-100"}`}>
                  <span>{c}</span>
                  <span className="text-[13px] tabular-nums text-slate-500">{counts.get(c) || 0}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="search-inventory-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari kamera, lensa, mic…"
              aria-label="Cari alat"
              className="h-[44px] rounded-lg border-slate-300 bg-white pl-10 text-[15px] text-slate-900"
            />
          </div>

          <div className="mb-3 mt-4 flex flex-wrap items-center gap-2.5">
            <p role="status" className="mr-auto text-sm tabular-nums text-slate-500">
              Menampilkan {shown.length} dari {items.length} alat
            </p>
            <label htmlFor="sort" className="sr-only">Urutkan katalog</label>
            <select id="sort" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Urutkan katalog"
              className="h-[44px] rounded-lg border border-slate-300 bg-white px-2.5 text-sm text-slate-900">
              <option value="nama-asc">Nama A–Z</option>
              <option value="nama-desc">Nama Z–A</option>
              <option value="sisa-desc">Sisa terbanyak</option>
            </select>
          </div>

          {loading ? (
            <p className="text-sm text-slate-500">Memuat alat…</p>
          ) : loadError ? (
            <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
              <p><strong>Gagal memuat data alat.</strong> Periksa koneksi lalu coba lagi.</p>
              <button onClick={load} className="mt-2 min-h-[44px] rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900">Coba lagi</button>
            </div>
          ) : shown.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-7 text-center">
              <p className="font-semibold text-slate-900">Tidak ada alat yang cocok.</p>
              <p className="mt-1 text-sm text-slate-500">Coba kata kunci lain atau pilih kategori berbeda.</p>
              <button onClick={() => { setQ(""); setCategory(""); }}
                className="mt-3 min-h-[44px] rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900">Tampilkan semua alat</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
              {shown.map((item) => {
                const qty = qtyOf(item.id);
                const maintenance = item.status === "MAINTENANCE";
                const available = item.available_qty;
                const st = stockText(item, sched);
                const conflict = sched.ok && available != null && qty > available;
                return (
                  <article key={item.id} data-testid={`item-card-${item.item_code}`}
                    className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
                    <Link to={`/barang/${item.id}`} onClick={rememberScroll} tabIndex={-1} aria-hidden="true"
                      className="block aspect-[4/3] w-full bg-slate-100">
                      <ProtectedImage src={item.photo} alt="" loading="lazy"
                        className="block aspect-[4/3] w-full object-cover" />
                    </Link>
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5 p-3">
                      <h3 className="text-[15px] font-semibold leading-snug text-slate-900">
                        <Link to={`/barang/${item.id}`} onClick={rememberScroll} className="hover:text-[#004f9e] hover:underline">
                          {item.name}
                        </Link>
                      </h3>
                      <p className="text-[13px] text-slate-500">
                        <code className="font-mono text-[0.92em]">{item.item_code}</code> · {item.category}
                      </p>
                      {(qty > 0 || maintenance || (sched.ok && (available == null || available <= 0)) || !sched.ok) && (
                        <p data-testid={`stock-${item.item_code}`} className={`text-[13px] ${toneClass[st.tone]}`}>{st.text}</p>
                      )}
                      <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
                        {maintenance ? (
                          <>
                            <span className="inline-block whitespace-nowrap rounded-full bg-red-50 px-2.5 py-[3px] text-xs font-semibold text-red-900">Maintenance</span>
                            <span className="text-[13px] text-slate-500">Tidak bisa dipinjam</span>
                          </>
                        ) : qty === 0 ? (
                          <button data-testid={`add-item-${item.item_code}`}
                            disabled={sched.ok && (available == null || available <= 0)}
                            title={sched.ok && available == null ? "Ketersediaan belum tersedia" : sched.ok && available <= 0 ? "Habis untuk jadwal ini" : undefined}
                            onClick={() => setQty(item, 1)}
                            className="min-h-[44px] px-2.5 py-2 text-sm font-semibold text-[#004f9e] transition-colors hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-45">
                            + Tambah
                          </button>
                        ) : (
                          <>
                            <div role="group" aria-label={`Jumlah ${item.name} di keranjang`}
                              className="inline-flex items-center gap-0.5 rounded-lg border border-slate-300">
                              <button data-testid={`dec-item-${item.item_code}`} aria-label={`Kurangi ${item.name}`}
                                onClick={() => setQty(item, qty - 1)}
                                className="h-[44px] w-[44px] rounded-lg text-xl leading-none text-slate-900 hover:bg-slate-100">−</button>
                              <output data-testid={`qty-${item.item_code}`} aria-label={`${qty} unit`} className="min-w-[30px] text-center text-[15px] font-bold tabular-nums">{qty}</output>
                              <button data-testid={`inc-item-${item.item_code}`} aria-label={`Tambah ${item.name}`}
                                disabled={sched.ok ? available == null || qty >= available : qty >= item.quantity}
                                title={sched.ok && available == null ? "Ketersediaan belum tersedia" : sched.ok && qty >= available ? "Sudah mencapai sisa untuk jadwal ini" : undefined}
                                onClick={() => setQty(item, qty + 1)}
                                className="h-[44px] w-[44px] rounded-lg text-xl leading-none text-slate-900 hover:bg-slate-100 disabled:opacity-45">+</button>
                            </div>
                            {conflict && (
                              <span className="inline-block whitespace-nowrap rounded-full border border-[#e8c95c] bg-[#fff8e6] px-2.5 py-[3px] text-xs font-semibold text-[#6b4e0c]">
                                Bentrok: butuh {qty}, sisa {available}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {totalQty > 0 && (
        <Link to="/checkout" data-testid="go-to-checkout-button"
          className="fixed left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 whitespace-nowrap rounded-full border border-[#0067c8] bg-[#0067c8] px-7 py-4 text-base font-semibold text-white shadow-2xl transition-colors duration-200 hover:border-[#004f9e] hover:bg-[#004f9e] [bottom:calc(4.5rem+env(safe-area-inset-bottom,0px))] md:bottom-8">
          <ShoppingBag className="h-5 w-5" />
          Lanjut Booking · {totalQty} pcs
        </Link>
      )}
    </div>
  );
}
